// CommentReply.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatDateTime } from 'utilities/DateTime';
import { getActivityCommentKey } from 'node/activity/utils/activityReactions';

// CSS
import './CommentReply.css';

// Icons
import Icon from 'svg/Icon';

// Components
import CommentActions from '../actions/CommentActions';
import CommentFooter from '../footer/CommentFooter';
import ActivityCommentAttachments from 'node/activity/items/ActivityCommentAttachments';
import ActivityReactionControl from 'node/activity/items/ActivityReactionControl';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * CommentReply component
 * 
 * Renders a reply to a comment or reply.
 *
 *
 * @returns {ReactNode} The rendered component.
 */
const CommentReply = ({
    node,
    reply,
    comment,
    comments,
    reactions,
    onReplyClicked,
    onDeleteClicked
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    const replyReactions = useMemo(() => {
      const commentKey = getActivityCommentKey(reply);
      return (Array.isArray(reactions) ? reactions : []).filter((row) => (
        String(row?.commentKey || '').trim() === commentKey
      ));
    }, [reactions, reply]);

    const timestampLabel = useMemo(() => {
        const raw = reply?.dateCreated;
        if (!raw) return '';
        const date = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        return formatDateTime(date);
    }, [reply?.dateCreated]);

    // ----- RENDER ----- //

    return (
        <div className="comment-reply-container" key={reply.key}>

            <ProfilePhoto node={reply.user} size={24} />

            {/* RIGHT */}
            <div className="comment-reply-right">

                {/* HEADER */}
                <div className="comment-reply-header">
                    <div className="comment-reply-header-meta">
                        {reply.parentKey === comment.key ? (
                            <span className="comment-reply-name">
                                {reply.user.name}
                            </span>
                        ) : (
                            <>
                                <span className="comment-reply-name">
                                    {reply.user.name}
                                </span>
                                <Icon
                                  iconKey="reply"
                                  color={theme.foregroundMuted}
                                  width={16}
                                  height={16}
                                />
                                <span className="comment-reply-name">
                                    {comments.find(c => c.key === reply.parentKey)?.user.name || ''}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="comment-reply-header-end">
                        <ActivityReactionControl
                          comment={reply}
                          node={node}
                          reactions={replyReactions}
                        />
                        {timestampLabel ? (
                            <span className="comment-reply-timestamp">
                                {timestampLabel}
                            </span>
                        ) : null}
                        <CommentActions
                          comment={reply}
                          onDeleteClicked={onDeleteClicked}
                        />
                    </div>
                </div>

                <div className="comment-text">
                    {reply.text}
                </div>

                <ActivityCommentAttachments
                  attachments={reply.attachments}
                  deleted={Boolean(reply.deleted)}
                />

                <div className="comment-footer">
                    <CommentFooter
                      node={node}
                      comment={reply}
                      onReplyClicked={onReplyClicked}
                    />
                </div>

            </div>

        </div>
    );
};

// Dev Tools
CommentReply.displayName = 'CommentReply';

export default CommentReply;
